import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, LinkButton, Tooltip } from '@etg/wings';
import { DangerousHtmlContent, LoadingDots, variants } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { CurrencyAmount } from '@eti/types';
import Media from 'react-media';
import { flexibleTicket, cancelForAnyReason } from '@eti/products';
import {
  RECOMMENED_FAREKEY,
  MANUALLY_ADDED_PRODUCTS,
  MANUALLY_REMOVED_PRODUCTS,
} from '../../../../constants/brandedFareConstants';
import PriceContentButton from './PriceContentButton';

const buttonStyles = css`
  margin-left: 0.625rem;
  width: 7.5rem;

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 0.625rem;
  }

  @media ${mediaQueries.medium.up} {
    width: 8.75rem;
  }

  @media ${mediaQueries.large.up} {
    width: 10rem;
  }
`;

const wrapperStyles = css`
  align-items: center;
  border-top: 1px dotted ${etiColors.grayScale[8]};
  display: flex;
  padding: 10px 10px 10px 20px;
  width: 100%;

  &:first-child {
    border-top: none;
  }

  [dir='rtl'] & {
    padding: 10px 20px 10px 10px;
  }

  @media ${mediaQueries.medium.up} {
    &:first-child {
      border-right: 1px dotted ${etiColors.grayScale[8]};
      &:last-child {
        border-right: none;
      }
    }

    /* stylelint-disable no-descending-specificity */
    [dir='rtl'] & {
      &:first-child {
        border-left: 1px dotted ${etiColors.grayScale[8]};
        border-right: none;
        &:last-child {
          border-left: none;
        }
      }
    }
    /* stylelint-enable no-descending-specificity */
  }

  @media ${mediaQueries.medium.up} {
    border-top: none;
  }

  @media ${mediaQueries.large.up} {
    padding: 20px;

    [dir='rtl'] & {
      padding: 20px;
    }
  }
`;

const deskTopTripTypeTitleStyles = css`
  margin-left: 10px;
  margin-right: auto;

  [dir='rtl'] & {
    margin-left: auto;
    margin-right: 10px;
  }
`;

const tripIconStyles = css`
  flex-shrink: 0;
  margin-right: auto;

  [dir='rtl'] & {
    margin-left: auto;
    margin-right: initial;
  }

  @media ${mediaQueries.xlarge.up} {
    margin-left: initial;
    margin-right: initial;

    [dir='rtl'] & {
      margin-left: initial;
      margin-right: initial;
    }
  }
`;

const ResultBookButton = ({
  addTrip,
  isDisabled,
  cfarTicketPrices,
  discountAmount,
  flexibleTicketPrices,
  isPrimaryButton,
  isRyanAirTicketingOrMarketingCarrierPresent,
  isSelectedTripRefetching,
  isVI,
  marketingCarrierNames,
  paymentMethodPrices,
  selectionKey,
  shouldDisplayBrandedFares,
  travelerPriceBreakdown = [],
  travelerPrices,
  tripIcon,
  tripTypeTitle,
  hasBookingButton,
  getAncillaryBundles,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const wrapperRef = useRef();

  const handleOnClick = async () => {
    if (!isLoading) {
      setIsLoading(true);
      try {
        await addTrip(
          {
            variables: {
              selectionKey,
              ...(flexibleTicketPrices && { extraProductSelection: [flexibleTicket.id] }),
              ...(cfarTicketPrices && { extraProductSelection: [cancelForAnyReason.id] }),
            },
          },
          getAncillaryBundles,
        );
      } finally {
        localStorage.removeItem(RECOMMENED_FAREKEY);
        localStorage.removeItem(MANUALLY_ADDED_PRODUCTS);
        localStorage.removeItem(MANUALLY_REMOVED_PRODUCTS);
        if (wrapperRef.current) {
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <div className={wrapperStyles} ref={wrapperRef}>
      <div className={tripIconStyles}>{tripIcon}</div>
      {cfarTicketPrices ? (
        <Media query={mediaQueries.xlarge.up}>
          {(matches) =>
            matches ? (
              <h3 className={deskTopTripTypeTitleStyles}>
                <Tooltip
                  content={
                    <DangerousHtmlContent content={t('Product.CancelForAnyReason.Tooltip')} />
                  }
                  title={tripTypeTitle}
                >
                  <LinkButton>{tripTypeTitle}</LinkButton>
                </Tooltip>
              </h3>
            ) : null
          }
        </Media>
      ) : (
        <Media query={mediaQueries.xlarge.up}>
          {(matches) =>
            matches ? <h3 className={deskTopTripTypeTitleStyles}>{tripTypeTitle}</h3> : null
          }
        </Media>
      )}
      <PriceContentButton
        cfarTicketPrices={cfarTicketPrices}
        discountAmount={discountAmount}
        flexibleTicketPrices={flexibleTicketPrices}
        isRyanAirTicketingOrMarketingCarrierPresent={isRyanAirTicketingOrMarketingCarrierPresent}
        isSelectTripLoading={isDisabled}
        isSelectedTripRefetching={isSelectedTripRefetching}
        isVI={isVI}
        marketingCarrierNames={marketingCarrierNames}
        paymentMethodPrices={paymentMethodPrices}
        shouldDisplayBrandedFares={shouldDisplayBrandedFares}
        travelerPriceBreakdown={travelerPriceBreakdown}
        travelerPrices={travelerPrices}
        tripTypeTitle={tripTypeTitle}
      />
      {hasBookingButton && (
        <Button
          className={buttonStyles}
          data-testid="resultPage-book-button"
          isDisabled={isDisabled}
          onClick={handleOnClick}
          style={{ flexShrink: 0 }}
          variant={isPrimaryButton ? variants.PRIMARY : undefined}
        >
          {isLoading ? <LoadingDots /> : t('General.Book')}
        </Button>
      )}
    </div>
  );
};

ResultBookButton.propTypes = {
  addTrip: PropTypes.func,
  cfarTicketPrices: PropTypes.number,
  discountAmount: PropTypes.number,
  flexibleTicketPrices: PropTypes.arrayOf(
    PropTypes.shape({
      price: PropTypes.shape({
        price: PropTypes.shape({
          value: PropTypes.number,
        }),
      }),
    }),
  ),
  getAncillaryBundles: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  hasBookingButton: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isPrimaryButton: PropTypes.bool,
  isRyanAirTicketingOrMarketingCarrierPresent: PropTypes.bool,
  isSelectedTripRefetching: PropTypes.bool,
  isVI: PropTypes.bool,
  marketingCarrierNames: PropTypes.string,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({ value: PropTypes.number })]),
    }),
  ),
  selectionKey: PropTypes.string,
  shouldDisplayBrandedFares: PropTypes.bool,
  travelerPriceBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      taxesAndFees: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string,
          taxes: PropTypes.arrayOf(
            PropTypes.shape({
              amount: CurrencyAmount,
              code: PropTypes.string,
              title: PropTypes.string,
            }),
          ),
        }),
      ),
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  travelerPrices: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  tripIcon: PropTypes.node,
  tripTypeTitle: PropTypes.string,
};

export default ResultBookButton;
